<template>
  <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-black text-white p-4">
    <div class="w-full max-w-5xl bg-gray-800 bg-opacity-95 p-6 rounded-2xl shadow-xl border border-gray-700 backdrop-blur-lg flex flex-row gap-8">
      
      <!-- Left Side: Logo & Info -->
      <div class="flex-1 flex flex-col items-center justify-center text-center">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="w-32 mb-4" />
        <h2 class="text-3xl font-extrabold text-blue-400">Join the Cloud</h2>
        <p class="text-gray-300 mt-2">Powerful cloud solutions at your fingertips.</p>
      </div>
      
      <!-- Right Side: Form -->
      <div class="flex-1">
        <form @submit.prevent="handleRegister" class="grid grid-cols-2 gap-4">
          
          <!-- Left Column Inputs -->
          <div class="col-span-2 sm:col-span-1">
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="name">Full Name</label>
            <input id="name" v-model="name" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Full Name" required />
          </div>
          
          <div class="col-span-2 sm:col-span-1">
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="email">Email</label>
            <input id="email" v-model="email" type="email" @input="validateEmail"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Email" required />
            <span v-if="emailError" class="text-red-500 text-sm">{{ emailError }}</span>
          </div>
          
          <div class="col-span-2">
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="address">Address</label>
            <input id="address" v-model="address" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Address" required />
          </div>
          
          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="city">City</label>
            <input id="city" v-model="city" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="City" required />
          </div>
          
          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="state">State</label>
            <input id="state" v-model="state" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="State" required />
          </div>
          
          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="zip">Zip</label>
            <input id="zip" v-model="zip" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Zip" required />
          </div>

          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="country">Country</label>
            <input id="country" v-model="country" type="text"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Country" required />
          </div>
          
          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="password">Password</label>
            <input id="password" v-model="password" type="password" @input="validatePassword"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Password" required />
            <span v-if="passwordError" class="text-red-500 text-sm">{{ passwordError }}</span>
          </div>
          
          <div>
            <label class="block text-gray-400 mb-1 text-sm font-medium" for="confirmPassword">Confirm Password</label>
            <input id="confirmPassword" v-model="confirmPassword" type="password" @input="validateConfirmPassword"
              class="w-full p-3 rounded-lg bg-gray-700 bg-opacity-80 border border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
              placeholder="Confirm Password" required />
            <span v-if="confirmPasswordError" class="text-red-500 text-sm">{{ confirmPasswordError }}</span>
          </div>
          
          <div class="col-span-2 flex items-center mt-2">
            <input id="tos" v-model="tosAccepted" type="checkbox" @change="validateForm" class="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500 focus:ring-2">
            <label for="tos" class="ml-2 text-sm text-gray-400">
              I agree to the <a href="#" class="text-blue-400 hover:underline">Terms of Service</a>
            </label>
          </div>
          
          <div class="col-span-2">
            <button type="submit"
              class="w-full py-3 bg-blue-600 rounded-lg text-lg font-semibold hover:bg-blue-700 transition duration-300 transform hover:scale-105 focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-md disabled:opacity-50 disabled:pointer-events-none"
              :disabled="!tosAccepted || !formValid">
              Register Now
            </button>
          </div>
          
        </form>
        <p class="text-center text-gray-400 mt-4 col-span-2">Already have an account? <a href="/login" class="text-blue-400 hover:underline">Log in</a></p>
      </div>
    </div>
    <!-- Toast Notifications -->
    <ToastManager ref="toastManager" />
  </div>
</template>

  
  <script>
  import axios from 'axios'; 
  import ToastManager from '@/components/ToastManager.vue';

  export default {
    name: "RegisterPage",
    data() {
      return {
        name: "",
        email: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        password: "",
        confirmPassword: "",
        tosAccepted: false,
        emailError: "",
        passwordError: "",
        confirmPasswordError: "",
        formValid: false
      };
    },
    components: {
        ToastManager
    },
    methods: {
      validateEmail() {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        if (!emailRegex.test(this.email)) {
          this.emailError = "Invalid email address.";
        } else {
          this.emailError = "";
        }
        this.validateForm();
      },
      validatePassword() {
        if (this.password.length < 6) {
          this.passwordError = "Password must be at least 6 characters long.";
        } else {
          this.passwordError = "";
        }
        this.validateForm();
      },
      validateConfirmPassword() {
        if (this.confirmPassword !== this.password) {
          this.confirmPasswordError = "Passwords do not match.";
        } else {
          this.confirmPasswordError = "";
        }
        this.validateForm();
      },
      validateForm() {
        this.formValid = !this.emailError && !this.passwordError && !this.confirmPasswordError && this.tosAccepted;
      },
      async handleRegister() {
        if (this.emailError || this.passwordError || this.confirmPasswordError) {
          alert("Please fix the errors before submitting.");
          return;
        }
  
        try {
          const response = await axios.post('https://api.syniac.com:3000/api/auth/register', {
            name: this.name,
            email: this.email,
            address: this.address,
            city: this.city,
            state: this.state,
            zip: this.zip,
            country: this.country,
            password: this.password,
          });

          console.log(response)
          this.$refs.toastManager.addToast('Your registration was successful.', 'success');
        } catch (error) {
          if (error.response) {
            this.$refs.toastManager.addToast('There was an error with your registration.', 'error');
          } else {
            this.$refs.toastManager.addToast('There was an error with your registration.', 'error');
          }
        }
      }
    }
  };
  </script>
  
